import React, {useState} from 'react';
import Input from "./Input";

// Types ----

export type Scheme = 'https' | 'mailto' | 'tel'

type SchemeOption = {
  value: Scheme
  name: string
}

type UrlInputProps = {
  name: string
  label?: string | null
  hint?: string | null
  value?: string | null
  onChange?: (value: string) => void
  schemes?: Scheme[]
  onChangeScheme?: (scheme: Scheme) => void
  help?: string | null
  error?: string | null
  disabled?: boolean
  required?: boolean
}

// Helpers ----

const validSchemes: Scheme[] = ["https", "mailto", "tel"]

const allOptions: SchemeOption[] = [
  {value: "https", name: "Web"},
  {value: "mailto", name: "Email"},
  {value: "tel", name: "Phone"},
]

function extractScheme(schemes: Scheme[], url: string): Scheme {
  const parts = url.split(':');
  const scheme = parts[0] ?? '';

  if(schemes.includes(scheme as Scheme)) return scheme as Scheme;

  return schemes[0];
}

function extractBody(url: string): string {
  const parts = url.split(':');
  parts.shift()

  if(parts[0]?.startsWith('//')) {
    parts[0] = parts[0].slice(2)
  }

  return parts.join(':')
}

// Component ----

function UrlInput({name, label, hint, value, onChange, schemes, onChangeScheme, help, error, disabled, required = false}: UrlInputProps) {

  const [scheme, setScheme] = useState<Scheme>(extractScheme(schemes ?? validSchemes, value ?? ''));
  const [body, setBody] = useState<string>(extractBody(value ?? ''));

  function changeUrl(scheme: Scheme, body: string) {
    const bodyPrefix = (scheme === 'https')? '//' : '';
    const newUrl = scheme + ':' + bodyPrefix + body;

    if(onChange) onChange(newUrl);
  }

  function changeScheme(value: Scheme) {
    setScheme(value)
    changeUrl(value, body)
    onChangeScheme?.(value);
  }

  function changeBody(value: string) {
    const includedScheme = (schemes ?? validSchemes).find(scheme => value.startsWith(scheme));

    const newScheme = includedScheme ?? scheme;
    const newBody = includedScheme? extractBody(value) : value;

    setScheme(newScheme)
    setBody(newBody)
    changeUrl(newScheme, newBody)
  }

  const options = allOptions.filter(option => !schemes || scheme.includes(option.value));

  const selectClasses = "h-full rounded-md border-0 bg-transparent py-0 pl-3 pr-7 text-gray-500 focus:outline-none " +
    "focus:ring-2 focus:ring-inset focus:ring-brand sm:text-sm"

  const schemeSelector = (
    <div className="absolute inset-y-0 left-0 flex items-center">
      <label htmlFor="country" className="sr-only">
        Scheme
      </label>

      <select
        id="scheme"
        name="scheme"
        className={selectClasses}
        value={scheme}
        onChange={event => changeScheme(event.currentTarget.value as Scheme)}
        disabled={disabled}
      >
        {options.map(({value, name}) => (
          <option value={value} key={value}>{name}</option>
        ))}
      </select>
    </div>
  );

  return (
    <Input
      type="text"
      name={name}
      label={label}
      value={body}
      onChange={event => changeBody(event.currentTarget.value)}
      hint={hint}
      help={help}
      error={error}
      leadingAddOn={(!schemes || schemes.length > 1) && schemeSelector}
      disabled={disabled}
      required={required}
    />
  );
}

// Export ----

export default UrlInput;